import { asImageSrc } from "@prismicio/client"
import React from "react"

import { DEFAULT_LANGUAGE } from "@bounce/i18n"
import { useReferrals } from "@bounce/referrals"

import { MainContent } from "./components/MainContent"
import { StructuredData } from "./components/StructuredData"
import { useBreadcrumbs } from "./hooks/useBreadcrumbs"
import { useHero } from "./hooks/useHero"
import { useInterpolations } from "./hooks/useInterpolations"
import { useSEO } from "./hooks/useSEO"
import { RichTextSection } from "@/components/Generic/RichText/RichTextSection/RichTextSection"
import { CityList } from "@/components/LuggageStorage/CityList/CityList"
import { HeroCityPoi } from "@/components/LuggageStorage/HeroCityPoi/HeroCityPoi"
import { Layout } from "@/components/LuggageStorage/Layout/Layout"
import { PoiChipNavSlider } from "@/components/LuggageStorage/PoiChipNavSlider/PoiChipNavSlider"
import { useSearchBar } from "@/components/LuggageStorage/SearchBar/useSearchBar"
import { StickyCTAProvider } from "@/contexts/StickyCTAContext"
import type { AggregateRating } from "@/libs/bounce/aggregateRating"
import type { CityData, RelatedPoi } from "@/libs/bounce/cities"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"
import { useCityUrl } from "@/libs/router"
import type { Review } from "@/utils/reviews"
import { extractFirstSection } from "@/utils/richText/filters"

export type CityScreenProps = {
  aggregateRating: AggregateRating
  city: CityData
  nearbyCities: (Pick<CityData, "id" | "translatedName"> & { url: string })[]
  reviews: Review[]
  reviewsForSlider: Review[]
  nearestMapStorePins: NearbyStorageSpot[]
  mainCityStores: NearbyStorageSpot[]
  openAllTimeNearestStores: NearbyStorageSpot[]
  nearestCityLockers: NearbyStorageSpot[]
  featuredPois: RelatedPoi[]
}

const CityScreen = ({
  aggregateRating,
  city,
  featuredPois,
  nearbyCities,
  reviews,
  reviewsForSlider,
  nearestMapStorePins,
  mainCityStores,
  openAllTimeNearestStores,
  nearestCityLockers,
}: CityScreenProps) => {
  const interpolations = useInterpolations({ city })
  const { metaTitle, metaDescription } = useSEO({ city, interpolations })
  const breadcrumbs = useBreadcrumbs({ city })
  const { absolutePath, getUrl } = useCityUrl({ city })

  const heroProps = useHero({ city, interpolations, aggregateRating })
  const { searchUrl } = useSearchBar({ initialQuery: heroProps.query, from: "City" })
  const { coupon } = useReferrals.useCodes()

  const alternates = city.alternateLanguages
    .map(language => ({ language, href: getUrl({ absolute: true, language }) }))
    .concat([{ language: "x-default", href: getUrl({ absolute: true, language: DEFAULT_LANGUAGE }) }])

  const filteredContent = extractFirstSection(city.content)

  return (
    <StickyCTAProvider
      config={{
        searchQuery: city.query,
        from: "City",
        coupon,
        label: heroProps.cta,
      }}>
      <Layout
        alternates={alternates}
        breadcrumbs={breadcrumbs}
        headProps={{
          title: metaTitle,
          description: metaDescription,
          url: absolutePath,
          image: asImageSrc(city.image, { ar: "1.91:1", fit: "crop" }),
        }}
        languages={city.alternateLanguages}
        from="City"
        searchQuery={city.query}
        hideNewsletterBanner
        hideDynamicAppBanner>
        <PoiChipNavSlider pois={city.pois} nearMeSearchUrl={searchUrl} from="City" />
        <HeroCityPoi {...heroProps} className="max-w-full" />
        <MainContent
          absolutePath={absolutePath}
          city={city}
          featuredPois={featuredPois}
          interpolations={interpolations}
          mainCityStores={mainCityStores}
          nearestCityLockers={nearestCityLockers}
          nearestMapStorePins={nearestMapStorePins}
          openAllTimeNearestStores={openAllTimeNearestStores}
          reviewsForSlider={reviewsForSlider}
          searchUrl={searchUrl}
        />
        {filteredContent && (
          <RichTextSection
            className="mt-section"
            field={filteredContent}
            interpolations={interpolations}
            data-nosnippet
            hidePublicationDate
          />
        )}
        <CityList cities={nearbyCities} className="py-section" />
        <StructuredData city={city} aggregateRating={aggregateRating} reviews={reviews} />
      </Layout>
    </StickyCTAProvider>
  )
}

export default CityScreen
